import {
  BulkSuborderStatusEnum,
  EntityTypeEnum,
  BulkSuborderNoteLookup,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  useGetBulkSuborders,
  exportBulkSuborders,
  getGetBulkSubordersQueryOptions,
} from "@/requests/gen/react-query/bulk-suborder";
import { ExportCSV } from "@/components/ui/csv-export";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamDateFilter,
  ParamClearButton,
  ParamDataTable,
} from "@/components/ui/param-data-table";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useGetEntities } from "@/requests/gen/react-query/entity";
import { format, parseISO } from "date-fns";
import { dataTableSearchParsers } from "@/components/ui/param-data-table";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { Badge } from "@/components/ui/badge";
import { BulkSuborderMetrics } from "./-cards";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/auth/use-auth";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  parseAsInteger,
  parseAsString,
  parseAsBoolean,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { Toggle } from "@/components/ui/toggle";
import { FlaskConical } from "lucide-react";
import {
  createFileRoute,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";

const bulkSuborderSearchParsers = {
  statuses: parseAsArrayOf(
    parseAsStringEnum(Object.values(BulkSuborderStatusEnum)),
  ),
  fulfiller_ids: parseAsArrayOf(parseAsInteger),
  target_ship_by_start: parseAsString,
  target_ship_by_end: parseAsString,
  is_sample: parseAsBoolean,
};

const bulkSuborderSearchAllParsers = {
  ...dataTableSearchParsers,
  ...bulkSuborderSearchParsers,
};

const loadBulkSuborderSearch = createLoader(bulkSuborderSearchAllParsers);

type BulkSuborderSearch = inferParserType<typeof bulkSuborderSearchAllParsers>;

function bulkSuborderQueryParams(
  p: BulkSuborderSearch,
): Parameters<typeof getGetBulkSubordersQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    bulk_suborder_statuses: p.statuses,
    fulfiller_ids: p.fulfiller_ids,
    target_ship_by_start: p.target_ship_by_start,
    target_ship_by_end: p.target_ship_by_end,
    is_sample: p.is_sample ?? undefined,
  };
}

export const Route = createFileRoute(
  "/_private/_fulfillment-360/bulk-suborder/lookup/",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBulkSubordersQueryOptions(
        bulkSuborderQueryParams(loadBulkSuborderSearch(location.searchStr)),
      ),
    ),
  component: BulkSuborderLookup,
});

// Define the columns for the table
interface BulkSuborderTableProps {
  link?: LinkProps;
  bulk_suborder_id: string;
  created_at: string;
  target_ship_by?: string | null;
  quantity: number;
  status: BulkSuborderStatusEnum | "";
  entity: string;
  fulfiller: string;
  note: BulkSuborderNoteLookup | null;
  is_sample: boolean;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<BulkSuborderTableProps>[] = [
  {
    header: "Bulk Suborder ID",
    accessorKey: "bulk_suborder_id",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span>{row.original.bulk_suborder_id}</span>
        {row.original.is_sample && <Badge variant="secondary">Sample</Badge>}
      </div>
    ),
  },
  {
    header: "Created At",
    accessorKey: "created_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
    enableSorting: true,
  },
  {
    header: "Target Ship",
    accessorKey: "target_ship_by",
    cell: ({ row }) => {
      const dateString = row.getValue("target_ship_by") as string;
      const status = row.original.status;
      if (!dateString) return "";

      const targetDate = parseISO(dateString);
      const now = new Date();
      const daysUntilTarget = Math.ceil(
        (targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      );

      const variant =
        status !== BulkSuborderStatusEnum.Open
          ? "outline"
          : now > targetDate
            ? "destructive"
            : daysUntilTarget <= 2
              ? "warning"
              : "success";

      return (
        <Badge variant={variant}>{format(targetDate, "MM-dd-yyyy")}</Badge>
      );
    },
    enableSorting: true,
  },
  {
    header: "Quantity",
    accessorKey: "quantity",
  },
  { header: "Status", accessorKey: "status" },
  {
    header: "Entity",
    accessorKey: "entity",
  },
  {
    header: "Fulfiller",
    accessorKey: "fulfiller",
  },
  {
    header: "Note",
    accessorKey: "note",
    cell: ({ row }) => {
      const note = row.getValue("note") as BulkSuborderNoteLookup | null;
      if (!note) return "--";
      return (
        <HoverCard>
          <HoverCardTrigger
            asChild
            className="max-w-50 truncate block cursor-pointer"
          >
            <span>{note.note}</span>
          </HoverCardTrigger>
          <HoverCardContent className="w-80 cursor-pointer">
            <div>{note.note}</div>
            <Separator className="my-2" />
            <div className="text-sm text-muted-foreground">
              {format(note.created_at, "MM-dd-yyyy h:mm a")} by{" "}
              {note.updated_by.first_name} {note.updated_by.last_name}
            </div>
          </HoverCardContent>
        </HoverCard>
      );
    },
  },
];

// Main component
function BulkSuborderLookup() {
  const { authUser } = useAuth();

  const [queryParams, setQueryParams] = useQueryStates(
    bulkSuborderSearchAllParsers,
  );

  // Fetch all entities to be filtered for the dropdown
  const { data: fulfillers } = useGetEntities({
    entity_types: [EntityTypeEnum.Fulfiller],
  });

  // Format fulfillers for the dropdown
  const fulfillerOptions = (fulfillers?.items || []).map((fulfiller) => ({
    label: fulfiller.name,
    value: fulfiller.entity_id,
  }));

  // Get the bulk_suborders, filtering by the selected fulfiller IDs
  const bulk_suborders = useGetBulkSuborders(
    bulkSuborderQueryParams(queryParams),
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const setOrderStatusFilter = (status: BulkSuborderStatusEnum) => {
    setQueryParams({
      statuses: [status],
      fulfiller_ids: authUser?.entity_ids?.[0]
        ? [authUser.entity_ids[0]]
        : null,
    });
  };

  const setOverdueStatusFilter = () => {
    setQueryParams({
      sort_column: "target_ship_by",
      ascending: true,
      statuses: [BulkSuborderStatusEnum.Open],
      fulfiller_ids: authUser?.entity_ids?.[0]
        ? [authUser.entity_ids[0]]
        : null,
      target_ship_by_end: new Date().toISOString(),
    });
  };

  // Map the data to the table format
  const tableData: BulkSuborderTableProps[] | undefined =
    bulk_suborders.data?.items.map((bulk_suborder) => {
      // Get the most recent note by creation date
      const mostRecentNote = bulk_suborder.bulk_suborder_notes?.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )?.[0];

      return {
        link: linkOptions({
          to: "/bulk-suborder/$bulkSuborderId",
          params: { bulkSuborderId: String(bulk_suborder.bulk_suborder_id) },
        }),
        bulk_suborder_id:
          bulk_suborder.bulk_order_id + "-" + bulk_suborder.bulk_suborder_id,
        created_at: bulk_suborder.created_at,
        target_ship_by: bulk_suborder.target_ship_by || null,
        quantity:
          bulk_suborder.bulk_suborder_products?.reduce(
            (acc, product) => acc + product.quantity,
            0,
          ) || 0,
        status: bulk_suborder.status,
        entity: bulk_suborder.bulk_order.entity.name,
        fulfiller: bulk_suborder.fulfiller.name,
        note: mostRecentNote || null,
        is_sample: bulk_suborder.is_sample ?? false,
      };
    });

  return (
    <div className="text-left">
      <title>Bulk Suborders - Legrande Health Portal</title>
      <Header
        crumbs={[
          { name: "Bulk Suborders", link: { to: "/bulk-suborder/lookup" } },
        ]}
      />
      <BulkSuborderMetrics
        onClickOpenSuborders={() =>
          setOrderStatusFilter(BulkSuborderStatusEnum.Open)
        }
        onClickOverdueSuborders={setOverdueStatusFilter}
      />
      <Card>
        <CardHeader>
          <CardTitle>Bulk Suborders</CardTitle>
          <CardDescription>
            View recent bulk suborders below. You can search, filter, and click
            to see more details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={bulk_suborders.isFetching} />
            <DropdownFilter
              title="Status"
              options={[
                {
                  items: Object.values(BulkSuborderStatusEnum)
                    .filter((status) => status !== BulkSuborderStatusEnum.Draft)
                    .map((status) => ({ label: status, value: status })),
                },
              ]}
              values={queryParams.statuses || []}
              onChange={(values) => setQueryParams({ statuses: values })}
            />
            <DropdownFilter
              title="Fulfiller"
              options={[{ items: fulfillerOptions }]}
              values={queryParams.fulfiller_ids || []}
              onChange={(values) => setQueryParams({ fulfiller_ids: values })}
            />
            <ParamDateFilter title="Target Ship Date" name="target_ship_by" />
            <Toggle
              variant="outline"
              pressed={queryParams.is_sample === true}
              onPressedChange={(pressed) =>
                setQueryParams({ is_sample: pressed ? true : null })
              }
            >
              <FlaskConical />
              Samples
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={bulk_suborders.isLoading}
            isError={bulk_suborders.isError}
            count={bulk_suborders.data?.count}
            footerActions={
              <ExportCSV
                onExport={() =>
                  exportBulkSuborders({
                    search: queryParams.search,
                    sort_column: queryParams.sort_column,
                    ascending: queryParams.ascending,
                    created_at_start: queryParams.created_at_start,
                    created_at_end: queryParams.created_at_end,
                    bulk_suborder_statuses: queryParams.statuses,
                    fulfiller_ids: queryParams.fulfiller_ids,
                    target_ship_by_start: queryParams.target_ship_by_start,
                    target_ship_by_end: queryParams.target_ship_by_end,
                    is_sample: queryParams.is_sample ?? undefined,
                    hidden_columns: queryParams.hidden,
                  })
                }
                filename="bulk-suborders"
                requiresPHIWarning={false}
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
